import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import Icon from '@common/components/icon';
import { TextInput } from '@common/components/form/inputs/text/text-input';
import { Select } from '@common/components/form/inputs/select/select-input';
import { combineClassNames } from '@utils/combineClassNames';
import { BrandLogo } from './brand-logo';
import { LocationsEditor, type EditableLocation } from './locations-editor';
import {
  ETrialIndustrySets,
  TRIAL_MODULE_KEYS,
  type TrialModuleKey,
  type TrialResearchPreview,
} from '../../types';

export const MAX_MODULES = 3;

/** One swatch per distinct colour: a site with a single brand colour gets one, not three. */
const uniqueColors = (preview: TrialResearchPreview): string[] => (
  preview.colors ? Array.from(new Set(Object.values(preview.colors).map((c) => c.toLowerCase()))) : []
);

export type PreviewValues = {
  name: string;
  industrySet: ETrialIndustrySets;
  languageLocale: 'nl' | 'en';
  locations: EditableLocation[];
  modules: TrialModuleKey[];
};

export const toEditableLocations = (
  preview: TrialResearchPreview,
): EditableLocation[] => preview.locations.map((location, index) => ({
  id: location.place_id ?? `found-${index}`,
  place_id: location.place_id,
  name: location.name,
  city: location.city,
  is_head_office: location.is_head_office,
  selected: true,
}));

type Props = {
  domain: string;
  preview: TrialResearchPreview;
  values: PreviewValues;
  onChange: (values: PreviewValues) => void;
  onContinue: () => void;
  onReject: () => void;
};

export const PreviewStep = ({
  domain, preview, values, onChange, onContinue, onReject,
}: Props) => {
  const { t } = useTranslation();
  const [editingLocations, setEditingLocations] = React.useState(false);

  const industryOptions = Object.values(ETrialIndustrySets).map((value) => ({
    value,
    label: t(`authentication:trial_industry_set_${value}`),
  }));
  const languageOptions = [
    { value: 'nl', label: 'Nederlands' },
    { value: 'en', label: 'English' },
  ];
  const selectedLocations = values.locations.filter((location) => location.selected);

  const toggleModule = (key: TrialModuleKey) => {
    const active = values.modules.includes(key);

    if (!active && values.modules.length >= MAX_MODULES) return;

    onChange({ ...values, modules: active ? values.modules.filter((m) => m !== key) : [...values.modules, key] });
  };

  return (
    <form
      className="Form"
      onSubmit={(e) => {
        e.preventDefault();
        if (values.name.trim()) onContinue();
      }}
    >
      <h2>{t('authentication:trial_preview_title', { domain })}</h2>
      <div className="Authentication__Form__Description">{t('authentication:trial_preview_description')}</div>

      <div className="FreeTrial__Grid">
        <div className="Form__Group">
          <label className="Form__Label" htmlFor="trial-name">{t('authentication:trial_preview_company_name')}</label>
          <TextInput
            id="trial-name"
            value={values.name}
            onChange={(e) => onChange({ ...values, name: e.target.value })}
            error={!values.name.trim()}
          />
        </div>
        <div className="Form__Group">
          <label className="Form__Label" htmlFor="trial-industry">{t('authentication:trial_preview_industry')}</label>
          <Select
            inputProps={{ id: 'trial-industry' }}
            value={values.industrySet}
            options={industryOptions}
            onChange={(option) => onChange({ ...values, industrySet: option?.value ?? ETrialIndustrySets.BASE })}
            clearable={false}
          />
        </div>
        <div className="Form__Group">
          <label className="Form__Label" htmlFor="trial-language">{t('authentication:trial_preview_language')}</label>
          <Select
            inputProps={{ id: 'trial-language' }}
            value={values.languageLocale}
            options={languageOptions}
            onChange={(option) => onChange({ ...values, languageLocale: option?.value ?? 'en' })}
            clearable={false}
          />
        </div>
      </div>

      <div className="FreeTrial__BrandRow">
        <BrandLogo iconUrl={preview.icon_url} logoUrl={preview.logo_url} name={values.name} />
        <div className="FreeTrial__BrandRow__Text">
          <div className="Form__Label">{t('authentication:trial_preview_brand')}</div>
          <div className="FreeTrial__Hint">{t('authentication:trial_preview_brand_hint')}</div>
        </div>
        <div className="FreeTrial__BrandRow__Swatches">
          {uniqueColors(preview).map((color) => (
            <span key={color} className="FreeTrial__Brand__Swatch" style={{ backgroundColor: color }} title={color} />
          ))}
        </div>
      </div>

      <div className="Form__Row">
        <div className="Form__Group">
          <div className="Form__Label">
            {t('authentication:trial_preview_modules_title')}
            <span className="FreeTrial__Hint">
              {' · '}
              {t('authentication:trial_preview_modules_hint')}
            </span>
          </div>
          <div className="FreeTrial__Chips">
            {TRIAL_MODULE_KEYS.map((key) => {
              const active = values.modules.includes(key);

              return (
                <button
                  key={key}
                  type="button"
                  className={combineClassNames('FreeTrial__Chip', { 'FreeTrial__Chip--active': active })}
                  aria-pressed={active}
                  disabled={!active && values.modules.length >= MAX_MODULES}
                  onClick={() => toggleModule(key)}
                >
                  {active && <Icon type="check" size="xsmall" />}
                  {t(`authentication:trial_module_${key}`)}
                </button>
              );
            })}
          </div>
          <div className="FreeTrial__Hint">{t('authentication:trial_preview_modules_note')}</div>
        </div>
      </div>

      <div className="FreeTrial__Actions">
        <a onClick={onReject} role="link">{t('authentication:trial_preview_not_my_company')}</a>
        <Button type="primary" buttonType="submit" iconRight="arrow_forward" disabled={!values.name.trim()}>
          <Trans i18nKey="authentication:trial_preview_continue" />
        </Button>
      </div>

      <div className="FreeTrial__Hint FreeTrial__LocationsSummary">
        <Icon type="place" size="xsmall" />
        {t('authentication:trial_preview_locations_found', { count: selectedLocations.length })}
        {' · '}
        <a onClick={() => setEditingLocations(true)} role="link">{t('authentication:trial_preview_locations_edit_title')}</a>
      </div>

      <LocationsEditor
        open={editingLocations}
        locations={values.locations}
        onChange={(locations) => onChange({ ...values, locations })}
        onClose={() => setEditingLocations(false)}
      />
    </form>
  );
};
